import type { FC } from 'react';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';

import SelectOutlined from 'src/assets/svg/select_outlined.svg?react';
import SelectOutlinedUnchecked from 'src/assets/svg/select_outlined_unchecked.svg?react';
import StarOutlined from 'src/assets/svg/star_outlined.svg?react';
import StarRounded from 'src/assets/svg/star_rounded.svg?react';
import type { ChatbotFeedbackValues } from 'src/types/chatbot';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';

import { chatbotMultiSelectOptions } from './helper';

import { styles } from './index.styles';

export interface ChatbotFeedbackFormProps {
  onCancelButtonClick: (e: React.MouseEvent<HTMLElement>) => void;
  setSubmitSuccessful: (x?: any) => void;
  setSubmitFailed: (x?: any) => void;
}

const ChatbotFeedbackForm: FC<ChatbotFeedbackFormProps> = ({
  onCancelButtonClick,
  setSubmitSuccessful,
  setSubmitFailed,
}) => {
  const { t } = useTranslation('chatbot');

  const [formValues, setFormValues] = useState<ChatbotFeedbackValues>({
    answer_too_short: false,
    answer_too_long: false,
    answer_seems_incomplete: false,
    answer_inappropriate: false,
    answer_has_wrong_info: false,
    answer_not_helpful: false,
    stars: 0,
  });

  const handleSubmitButtonClick = async () => {
    try {
      const response = await HttpService.post(
        apiPaths.VIRTUAL_ASSISTANT.FEEDBACK,
        formValues,
      );

      if (response.status === 200) {
        setSubmitSuccessful(true);
        setSubmitFailed(false);
        return;
      }

      setSubmitFailed(true);
    } catch (_error) {
      setSubmitFailed(true);
    }
  };

  const handleOnRatingClick = (e: any, newValue: number | null) =>
    setFormValues({
      ...formValues,
      stars: newValue,
    });

  const handleCheckboxSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.checked,
    }));
  };

  return (
    <Box sx={styles.feedbackForm}>
      <Grid sx={{ pb: 2 }}>
        <Typography variant="button2">
          {t('ChatbotFeedback.RateThisAnswer')}
        </Typography>
      </Grid>
      <Grid sx={{ pb: 3 }}>
        <Rating
          size="large"
          icon={<StarRounded />}
          emptyIcon={<StarOutlined />}
          value={formValues.stars}
          onChange={handleOnRatingClick}
        />
      </Grid>
      <Grid sx={{ pb: 1 }}>
        <Typography variant="button2" sx={{ pb: 8 }}>
          {t('ChatbotFeedback.ThisAnswerIs')}
        </Typography>
        <FormGroup>
          <Grid container>
            {chatbotMultiSelectOptions.map(({ label, name }, i) => (
              <Grid item xs={6} key={i}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="medium"
                      icon={
                        <SelectOutlinedUnchecked
                          width="1.5rem"
                          height="1.5rem"
                        />
                      }
                      checkedIcon={
                        <SelectOutlined width="1.5rem" height="1.5rem" />
                      }
                      onChange={handleCheckboxSelection}
                      sx={styles.checkbox}
                      disableRipple
                    />
                  }
                  label={<Typography variant="body4">{t(label)}</Typography>}
                  checked={formValues[name] || false}
                  value={formValues[name]}
                  name={name}
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </Grid>
      <Grid>
        <Stack direction="column" spacing={1} sx={{ pt: 3 }}>
          <Button
            disabled={
              (formValues.stars === 0 || formValues.stars === null) &&
              !Object.values(formValues).some((checked) => checked)
            }
            onClick={handleSubmitButtonClick}
            sx={styles.submitButton}
            fullWidth
          >
            {t('ChatbotFeedback.Submit')}
          </Button>
          <Button
            sx={styles.cancelButton}
            fullWidth
            onClick={onCancelButtonClick}
          >
            {t('ChatbotFeedback.Cancel')}
          </Button>
        </Stack>
      </Grid>
    </Box>
  );
};

export default ChatbotFeedbackForm;
